import { useEffect, useState, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Inbox, MessageSquare, Send, Loader2, Mail, Phone, MessageCircle,
  FileText, Smartphone, User, ArrowRight,
} from "lucide-react";
import {
  inboxListThreads, inboxGetThread, inboxReply, inboxSetStage,
} from "@/lib/api";
import { toast } from "sonner";
import InfographicHero from "@/components/InfographicHero";
import InboxLiveWireCard from "@/components/InboxLiveWireCard";
import MissedCallsCard from "@/components/MissedCallsCard";
import EmailSignatureCard from "@/components/EmailSignatureCard";

/** Unified Lead Inbox — /inbox */

const CHANNEL_ICON = {
  web_form: FileText, chat: MessageSquare, sms: Smartphone,
  email: Mail, whatsapp: MessageCircle, phone: Phone,
};
const STAGE_TONE = {
  new: "bg-sky-50 border-sky-200 text-sky-800",
  qualifying: "bg-amber-50 border-amber-200 text-amber-800",
  quoted: "bg-violet-50 border-violet-200 text-violet-800",
  won: "bg-emerald-50 border-emerald-200 text-emerald-800",
  lost: "bg-slate-50 border-slate-200 text-slate-500",
};

export default function UnifiedInboxPage() {
  const [threads, setThreads] = useState([]);
  const [counts, setCounts] = useState({});
  const [unread, setUnread] = useState(0);
  const [stages, setStages] = useState([]);
  const [active, setActive] = useState(null);   // {thread, messages}
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await inboxListThreads();
      setThreads(r.threads || []);
      setCounts(r.counts_by_stage || {});
      setUnread(r.unread || 0);
      setStages(r.pipeline_stages || []);
    } catch (e) {
      toast.error("Sign in required to view your inbox.");
    } finally { setLoading(false); }
  }, []);

  const openThread = async (t) => {
    try {
      const r = await inboxGetThread(t._id);
      setActive(r);
      await load();
    } catch (e) { toast.error("Could not open thread"); }
  };

  const sendReply = async () => {
    if (!active || !reply.trim()) return;
    setSending(true);
    try {
      await inboxReply(active.thread._id, reply.trim());
      setReply("");
      const r = await inboxGetThread(active.thread._id);
      setActive(r);
      toast.success("Reply sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const changeStage = async (stage) => {
    if (!active) return;
    try {
      await inboxSetStage(active.thread._id, stage);
      setActive({ ...active, thread: { ...active.thread, stage } });
      await load();
    } catch { toast.error("Could not update stage"); }
  };

  useEffect(() => {
    document.title = "Unified Lead Inbox — WebsiteTech.pro";
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="inbox-page">
      <Navbar />
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Inbox className="h-4 w-4" /> Unified Lead Inbox
          </div>
          <h1 className="mt-2 font-heading text-3xl font-black text-slate-900 sm:text-4xl">
            One thread. Every channel. Every lead.
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Website forms, chat, SMS, WhatsApp, email and phone collapse into a
            single conversation with pipeline stages and missed-call text-back.
            <b> Unread: {unread}.</b>
          </p>
          <div className="mt-6">
            <InfographicHero slug="unified-lead-customer-hub" hint={false} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {stages.map((s) => (
              <span key={s} className={`rounded-full border px-3 py-1 text-xs ${STAGE_TONE[s]}`} data-testid={`inbox-count-${s}`}>
                {s} <b>{counts[s] || 0}</b>
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-6 px-4 py-8 lg:grid-cols-[380px_1fr]">
        <div className="space-y-2 lg:col-span-2">
          <InboxLiveWireCard />
          <MissedCallsCard />
          <EmailSignatureCard />
        </div>
        <div className="space-y-2">
          {loading && <div className="text-slate-500">Loading…</div>}
          {!loading && threads.length === 0 && (
            <Card className="p-6 text-sm text-slate-500" data-testid="inbox-empty">
              No leads yet. Connect a channel to start collecting.
            </Card>
          )}
          {threads.map((t) => {
            const Icon = CHANNEL_ICON[t.channel] || MessageSquare;
            return (
              <Card
                key={t._id}
                onClick={() => openThread(t)}
                className={`cursor-pointer p-3 transition hover:shadow-md ${active?.thread._id === t._id ? "ring-2 ring-orange-400" : ""} ${t.unread ? "bg-white" : "bg-slate-50"}`}
                data-testid={`inbox-thread-${t._id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-slate-400" />
                      <div className="truncate font-semibold text-slate-900">{t.contact_name}</div>
                      {t.unread && <span className="h-2 w-2 rounded-full bg-orange-500" />}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-slate-500">{t.subject}</div>
                    <div className="mt-1 truncate text-[11px] font-mono text-slate-400">{t.contact_handle}</div>
                    {(t.intent || t.urgency) && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {t.intent && (
                          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-slate-600">
                            {t.intent.replace(/_/g, " ")}
                          </span>
                        )}
                        {t.urgency === "high" && (
                          <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-rose-700">
                            urgent
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <Badge variant="outline" className={`text-[10px] uppercase ${STAGE_TONE[t.stage]}`}>
                    {t.stage}
                  </Badge>
                </div>
              </Card>
            );
          })}
        </div>

        <div>
          {!active ? (
            <Card className="flex h-full items-center justify-center p-12 text-slate-400" data-testid="inbox-empty-detail">
              <div className="text-center">
                <MessageSquare className="mx-auto h-8 w-8" />
                <div className="mt-2 text-sm">Select a thread to view the full conversation.</div>
              </div>
            </Card>
          ) : (
            <Card className="flex h-full flex-col p-4" data-testid="inbox-thread-detail">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-3">
                <div>
                  <div className="flex items-center gap-2 font-semibold text-slate-900">
                    <User className="h-4 w-4" /> {active.thread.contact_name}
                  </div>
                  <div className="text-xs text-slate-500">
                    {active.thread.subject} · {active.thread.contact_handle}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1" data-testid="inbox-stage-toggle">
                  {stages.map((s) => (
                    <button
                      key={s}
                      onClick={() => changeStage(s)}
                      className={`rounded-full border px-3 py-1 text-[10px] uppercase transition ${STAGE_TONE[s]} ${active.thread.stage === s ? "ring-2 ring-offset-1 ring-orange-400" : "opacity-60 hover:opacity-100"}`}
                      data-testid={`inbox-stage-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-3 flex-1 space-y-2 overflow-y-auto">
                {active.messages.map((m) => (
                  <div key={m._id}
                    className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${m.direction === "in"
                      ? "bg-slate-100 text-slate-900"
                      : "ml-auto bg-orange-600 text-white"}`}>
                    <div className="whitespace-pre-wrap">{m.body}</div>
                    <div className={`mt-1 text-[10px] uppercase tracking-wider ${m.direction === "in" ? "text-slate-500" : "text-orange-200"}`}>
                      {m.channel} · {new Date(m.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 border-t border-slate-200 pt-3">
                <Textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder={`Reply via ${active.thread.channel.replace("_", " ")}…`}
                  className="min-h-[70px]"
                  data-testid="inbox-reply-input"
                />
                <div className="mt-2 flex justify-end">
                  <Button
                    onClick={sendReply}
                    disabled={sending || !reply.trim()}
                    className="bg-slate-900 text-white hover:bg-slate-800"
                    data-testid="inbox-reply-send"
                  >
                    {sending
                      ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      : <Send className="mr-1 h-4 w-4" />}
                    Send
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
